// @ts-nocheck
// TODO: tighten types after migration
import React, { useState, useEffect, useRef, useCallback } from 'react';
import FullScreenImageViewer from './FullScreenImageViewer';

const InlineMediaVideo = ({ src, title, poster, showTitle = true, style, isParentFullScreen = false }) => {
    const playerRef = useRef(null);
    const videoRef = useRef(null);
    const [isVideoReady, setIsVideoReady] = useState(false);
    const [isStarted, setIsStarted] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isBuffering, setIsBuffering] = useState(false);
    const [showSpeedMenu, setShowSpeedMenu] = useState(false);
    const [showSpeedSubmenu, setShowSpeedSubmenu] = useState(false);
    const [speed, setSpeed] = useState(1);
    const [duration, setDuration] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [isMuted, setIsMuted] = useState(false);
    const [volume, setVolume] = useState(1);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [triggerRect, setTriggerRect] = useState(null);
    const seekPercent = duration > 0 ? Math.min(100, Math.max(0, (currentTime / duration) * 100)) : 0;

    const handleMaximize = useCallback(() => {
        if (playerRef.current) {
            setTriggerRect(playerRef.current.getBoundingClientRect());
        }
        setIsFullScreen(true);
    }, []);

    useEffect(() => {
        if ((isFullScreen || isParentFullScreen) && videoRef.current) {
            videoRef.current.pause();
        }
    }, [isFullScreen, isParentFullScreen]);

    const [showControls, setShowControls] = useState(true);
    const controlsTimeoutRef = useRef(null);
    const hasAutoplayedRef = useRef(false);

    const resetControlsTimeout = useCallback(() => {
        if (controlsTimeoutRef.current) {
            clearTimeout(controlsTimeoutRef.current);
        }
        if (isPlaying) {
            controlsTimeoutRef.current = setTimeout(() => {
                setShowControls(false);
            }, 3000);
        }
    }, [isPlaying]);

    useEffect(() => {
        if (isPlaying) {
            resetControlsTimeout();
        } else {
            setShowControls(true);
        }
        return () => {
            if (controlsTimeoutRef.current) {
                clearTimeout(controlsTimeoutRef.current);
            }
        };
    }, [isPlaying, resetControlsTimeout]);

    // Reset hasAutoplayedRef and check visibility when src changes to autoplay on slide transition
    useEffect(() => {
        hasAutoplayedRef.current = false;
        
        const timer = setTimeout(() => {
            if (!videoRef.current || hasAutoplayedRef.current) return;
            const rect = videoRef.current.getBoundingClientRect();
            const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
            if (isVisible) {
                hasAutoplayedRef.current = true;
                setIsStarted(true);
                videoRef.current.play().catch((err) => {
                    console.log("Autoplay check visibility failed, trying muted:", err);
                    if (videoRef.current) {
                        videoRef.current.muted = true;
                        setIsMuted(true);
                        videoRef.current.play().catch(e => console.log("Muted autoplay failed", e));
                    }
                });
            }
        }, 150);
        
        return () => clearTimeout(timer);
    }, [src]);

    // IntersectionObserver to autoplay one time when scrolled into view
    useEffect(() => {
        if (!videoRef.current) return;
        
        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting && !hasAutoplayedRef.current && videoRef.current) {
                    hasAutoplayedRef.current = true;
                    setIsStarted(true);
                    videoRef.current.play().catch((err) => {
                        console.log("Autoplay blocked, trying muted:", err);
                        if (videoRef.current) {
                            videoRef.current.muted = true;
                            setIsMuted(true);
                            videoRef.current.play().catch(e => console.log("Muted autoplay failed", e));
                        }
                    });
                    observer.unobserve(entry.target);
                }
            });
        }, { threshold: 0.5 });

        observer.observe(videoRef.current);
        return () => {
            observer.disconnect();
        };
    }, [src]);

    const handleVideoClick = useCallback((e) => {
        e.stopPropagation();
        setShowControls((prev) => {
            const nextVal = !prev;
            if (nextVal) {
                resetControlsTimeout();
            }
            return nextVal;
        });
    }, [resetControlsTimeout]);

    const handleMouseMove = useCallback(() => {
        setShowControls(true);
        resetControlsTimeout();
    }, [resetControlsTimeout]);

    const handleMouseLeave = useCallback(() => {
        if (isPlaying) {
            setShowControls(false);
        }
    }, [isPlaying]);

    const formatTime = useCallback((seconds) => {
        const safe = Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0;
        const mins = Math.floor(safe / 60);
        const secs = safe % 60;
        return `${mins}:${String(secs).padStart(2, '0')}`;
    }, []);

    const applySpeed = useCallback((nextSpeed) => {
        setSpeed(nextSpeed);
        if (videoRef.current) {
            videoRef.current.playbackRate = nextSpeed;
        }
        setShowSpeedSubmenu(false);
        setShowSpeedMenu(false);
    }, []);

    const handleSeek = useCallback((event) => {
        if (!videoRef.current) return;
        const seekTime = Number(event.target.value);
        videoRef.current.currentTime = seekTime;
        setCurrentTime(seekTime);
    }, []);

    const toggleMute = useCallback(() => {
        if (!videoRef.current) return;
        const nextMuted = !isMuted;
        videoRef.current.muted = nextMuted;
        setIsMuted(nextMuted);
    }, [isMuted]);

    const handleVolumeChange = useCallback((event) => {
        if (!videoRef.current) return;
        const nextVolume = Number(event.target.value);
        videoRef.current.volume = nextVolume;
        videoRef.current.muted = false;
        setIsMuted(false);
        setVolume(nextVolume);
    }, []);

    const handlePlayPause = useCallback(() => {
        if (!videoRef.current) return;
        if (videoRef.current.paused) {
            setIsStarted(true);
            videoRef.current.play();
        } else {
            videoRef.current.pause();
        }
    }, []);

    useEffect(() => {
        setIsVideoReady(false);
        setIsBuffering(false);
    }, [src]);

    useEffect(() => {
        const handleOutsideClick = (event) => {
            if (!playerRef.current) return;
            if (!playerRef.current.contains(event.target)) {
                setShowSpeedMenu(false);
                setShowSpeedSubmenu(false);
            }
        };

        document.addEventListener('mousedown', handleOutsideClick);
        document.addEventListener('touchstart', handleOutsideClick);

        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
            document.removeEventListener('touchstart', handleOutsideClick);
        };
    }, []);

    return (
        <>
        <div 
            style={{ margin: '1.25rem 0', width: '100%', ...style }}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
        >
            <div ref={playerRef} style={{ position: 'relative', width: '100%', borderRadius: '14px', overflow: 'hidden', background: '#000' }}>
                <video
                    ref={videoRef}
                    src={src}
                    poster={poster || undefined}
                    preload="metadata"
                    playsInline
                    controls={false}
                    muted={isMuted}
                    onLoadedMetadata={() => {
                        if (!videoRef.current) return;
                        setDuration(videoRef.current.duration || 0);
                        setCurrentTime(videoRef.current.currentTime || 0);
                        setIsVideoReady(true);
                    }}
                    onLoadedData={() => setIsVideoReady(true)}
                    onCanPlay={() => {
                        setIsVideoReady(true);
                        setIsBuffering(false);
                    }}
                    onError={() => setIsVideoReady(true)}
                    onTimeUpdate={() => {
                        if (!videoRef.current) return;
                        setCurrentTime(videoRef.current.currentTime || 0);
                        if (isBuffering) {
                            setIsBuffering(false);
                        }
                    }}
                    onVolumeChange={() => {
                        if (!videoRef.current) return;
                        setVolume(videoRef.current.volume);
                        setIsMuted(videoRef.current.muted);
                    }}
                    onPlay={() => {
                        setIsPlaying(true);
                        setIsBuffering(false);
                    }}
                    onPlaying={() => {
                        setIsPlaying(true);
                        setIsBuffering(false);
                    }}
                    onWaiting={() => {
                        if (!videoRef.current?.paused) {
                            setIsBuffering(true);
                        }
                    }}
                    onStalled={() => {
                        if (!videoRef.current?.paused) {
                            setIsBuffering(true);
                        }
                    }}
                    onSeeking={() => {
                        if (!videoRef.current?.paused) {
                            setIsBuffering(true);
                        }
                    }}
                    onSeeked={() => setIsBuffering(false)}
                    onPause={() => {
                        setIsPlaying(false);
                        setIsBuffering(false);
                    }}
                    onEnded={() => {
                        setIsPlaying(false);
                        setIsBuffering(false);
                    }}
                    style={{ width: '100%', display: 'block', cursor: 'pointer', opacity: isVideoReady ? 1 : 0.2 }}
                    onClick={handleVideoClick}
                />

                {!isVideoReady && (
                    <div
                        className="blog-image-skeleton-loader"
                        style={{
                            position: 'absolute',
                            inset: 0,
                            margin: 0,
                            minHeight: 'unset',
                            height: '100%',
                            aspectRatio: 'auto',
                            borderRadius: '14px',
                            zIndex: 2
                        }}
                    ></div>
                )}

                {!isStarted && (
                    <button
                        type="button"
                        onClick={handlePlayPause}
                        aria-label="Play video"
                        style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: '78px',
                            height: '78px',
                            borderRadius: '999px',
                            border: 'none',
                            background: 'rgba(0,0,0,0.55)',
                            color: '#fff',
                            fontSize: '30px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer'
                        }}
                    >
                        <i className="fi fi-sr-play"></i>
                    </button>
                )}

                {isStarted && isPlaying && isBuffering && (
                    <div
                        style={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            pointerEvents: 'none',
                            zIndex: 4
                        }}
                    >
                        <span className="inline-video-buffer-loader" aria-hidden="true"></span>
                    </div>
                )}

                <div 
                    onClick={(e) => e.stopPropagation()}
                    style={{ 
                        position: 'absolute', 
                        left: '0', 
                        right: '0', 
                        bottom: '0', 
                        padding: '10px 12px', 
                        background: 'linear-gradient(0deg, rgba(0,0,0,0.8), rgba(0,0,0,0.05))',
                        transition: 'opacity 0.3s ease, visibility 0.3s ease',
                        opacity: showControls ? 1 : 0,
                        visibility: showControls ? 'visible' : 'hidden',
                        zIndex: 10
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <button
                        type="button"
                        onClick={handlePlayPause}
                        style={{ border: 'none', borderRadius: '8px', padding: '6px 10px', background: 'rgba(0,0,0,0.55)', color: '#fff', cursor: 'pointer' }}
                    >
                        {isPlaying ? <i className="fi fi-sr-pause"></i> : <i className="fi fi-sr-play"></i>}
                    </button>

                    <span style={{ color: '#fff', fontSize: '13px', minWidth: '86px' }}>
                        {formatTime(currentTime)} / {formatTime(duration)}
                    </span>

                    <input
                        type="range"
                        min={0}
                        max={duration || 0}
                        step={0.01}
                        value={currentTime}
                        onChange={handleSeek}
                        style={{
                            flex: 1,
                            minWidth: '180px',
                            margin: 0,
                            cursor: 'pointer',
                            height: '6px',
                            borderRadius: '999px',
                            appearance: 'none',
                            background: `linear-gradient(to right, #47b5ff 0%, #47b5ff ${seekPercent}%, rgba(255,255,255,0.35) ${seekPercent}%, rgba(255,255,255,0.35) 100%)`,
                            accentColor: '#47b5ff'
                        }}
                    />

                    <button
                        type="button"
                        onClick={toggleMute}
                        style={{ border: 'none', borderRadius: '8px', padding: '6px 10px', background: 'rgba(0,0,0,0.55)', color: '#fff', cursor: 'pointer' }}
                        aria-label={isMuted ? 'Unmute' : 'Mute'}
                    >
                        {isMuted ? <i className="fi fi-sr-volume-slash"></i> : <i className="fi fi-sr-volume"></i>}
                    </button>

                    {!isMuted && (
                        <input
                            type="range"
                            min={0}
                            max={1}
                            step={0.01}
                            value={volume}
                            onChange={handleVolumeChange}
                            style={{ width: '41px', margin: 0, cursor: 'pointer', accentColor: '#47b5ff' }}
                            aria-label="Volume"
                        />
                    )}

                    <div style={{ marginLeft: 'auto', position: 'relative' }}>
                    <button
                        type="button"
                        onClick={() => {
                            setShowSpeedMenu((v) => !v);
                            setShowSpeedSubmenu(false);
                        }}
                        aria-label="Video options"
                        style={{ border: 'none', borderRadius: '8px', padding: '6px 10px', background: 'rgba(0,0,0,0.55)', color: '#fff', cursor: 'pointer' }}
                    >
                        <i className="fi fi-br-menu-dots"></i>
                    </button>

                    {showSpeedMenu && (
                        <div style={{ position: 'absolute', bottom: '42px', right: '0', minWidth: '140px', borderRadius: '10px', background: '#eef0fb', boxShadow: '0 10px 25px rgba(0,0,0,0.25)', padding: '6px' }}>
                            {!showSpeedSubmenu ? (
                                <button
                                    type="button"
                                    onClick={() => setShowSpeedSubmenu(true)}
                                    style={{
                                        width: '100%',
                                        textAlign: 'left',
                                        border: 'none',
                                        borderRadius: '8px',
                                        padding: '10px 12px',
                                        background: 'transparent',
                                        color: '#111',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center'
                                    }}
                                >
                                    <span>Speed</span>
                                    <span>{speed}x</span>
                                </button>
                            ) : (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => setShowSpeedSubmenu(false)}
                                        style={{
                                            width: '100%',
                                            textAlign: 'left',
                                            border: 'none',
                                            borderRadius: '8px',
                                            padding: '10px 12px',
                                            background: 'transparent',
                                            color: '#111',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        ← Speed
                                    </button>
                                    {[0.5, 1, 1.25, 1.5, 2].map((rate) => (
                                        <button
                                            key={rate}
                                            type="button"
                                            onClick={() => applySpeed(rate)}
                                            style={{
                                                width: '100%',
                                                textAlign: 'left',
                                                border: 'none',
                                                borderRadius: '8px',
                                                padding: '10px 12px',
                                                background: rate === speed ? '#dbe5ff' : 'transparent',
                                                color: '#111',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            {rate}x
                                        </button>
                                    ))}
                                </>
                            )}
                        </div>
                    )}
                    </div>
                    </div>
                </div>
            </div>
            {showTitle && title && (
                <div className="mongodb-image-info-row flex justify-between items-start w-full gap-4 mt-3">
                    <p className="mongodb-image-title flex-1 m-0 text-left italic border-r-[3.5px] border-[#ccc] pr-3 mr-2 leading-relaxed" style={{ fontFamily: 'var(--post-content-font)' }}>
                        {title}
                    </p>
                    <div className="mongodb-image-post-controls flex-shrink-0 flex items-center gap-2">
                        <button 
                            className="mongodb-nav-btn maximize-btn appearance-none bg-white border border-[#e1e1e1] rounded-[8px] w-8 h-8 flex items-center justify-center cursor-pointer transition-all duration-200 shadow-[0_2px_4px_rgba(0,0,0,0.08)] p-0 relative outline-none hover:translate-y-[-1px] hover:shadow-[0_3px_6px_rgba(0,0,0,0.12)] hover:border-[#d1d1d1] hover:bg-[#fdfdfd] active:translate-y-0 active:shadow-[0_1px_2px_rgba(0,0,0,0.1)] disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={handleMaximize}
                            aria-label="Maximize video"
                        >
                            <i className="fi fi-br-expand text-[12px] leading-[1] flex items-center justify-center text-[#555] w-full h-full m-t-1"></i>
                        </button>
                    </div>
                </div>
            )}
        </div>
        <FullScreenImageViewer
            isOpen={isFullScreen}
            onClose={() => setIsFullScreen(false)}
            images={[{ src, title, isVideo: true }]}
            initialIndex={0}
            triggerRect={triggerRect}
        />
        </>
    );
};

export default InlineMediaVideo;
